import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRetryCountdown } from "@/hooks/useRetryCountdown";
import { BLOG_CMS_COPY } from "@/lib/blog-cms/copy";
import { classifyTicketFailure } from "@/lib/blog-cms/protocol";
import { requestBlogCmsTicket } from "@/services/blog-cms";
import { RetryCountdownText } from "./RetryCountdownText";

type FocusTarget = "copy" | "close" | "create" | "title";

interface AgentLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The overflow-menu trigger: the menu item that opened the dialog no longer exists. */
  returnFocusRef: React.RefObject<HTMLElement | null>;
}

function RateLimitedCreate({
  seconds,
  onCreate,
  ref,
}: {
  seconds: number;
  onCreate: () => void;
  ref: React.Ref<HTMLButtonElement>;
}) {
  const secondsLeft = useRetryCountdown(seconds);
  return (
    <div className="flex flex-col gap-2">
      <RetryCountdownText secondsLeft={secondsLeft} />
      <Button ref={ref} variant="outline" onClick={onCreate} disabled={secondsLeft > 0}>
        {BLOG_CMS_COPY.agentCreate}
      </Button>
    </div>
  );
}

/**
 * The dialog body. It unmounts when the dialog closes, so closing clears the url, the status and
 * any timer with it.
 */
function AgentLinkBody({ onClose }: { onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [expiresInMs, setExpiresInMs] = useState(0);
  const [pending, setPending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState("");
  const [rateLimit, setRateLimit] = useState<{ seconds: number; key: number } | null>(null);
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; n: number } | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const createRef = useRef<HTMLButtonElement | null>(null);
  const copyRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const failuresRef = useRef(0);

  const requestFocus = (target: FocusTarget) =>
    setFocusRequest((previous) => ({ target, n: (previous?.n ?? 0) + 1 }));

  useEffect(() => {
    if (focusRequest === null) return;
    const refs = { copy: copyRef, close: closeRef, create: createRef, title: titleRef };
    refs[focusRequest.target].current?.focus();
  }, [focusRequest]);

  // The link is cleared at `expiresIn`: the ticket inside it is dead by then.
  useEffect(() => {
    if (url === null) return;
    const timer = setTimeout(() => {
      setUrl(null);
      setStatus(BLOG_CMS_COPY.agentExpired);
    }, expiresInMs);
    return () => clearTimeout(timer);
  }, [url, expiresInMs]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  async function handleCreate() {
    const controller = new AbortController();
    controllerRef.current = controller;
    setPending(true);
    setCopied(false);
    setStatus("");
    try {
      // The agent ticket's C5 lands in the agent's own profile, so its origin is not remembered.
      const ticket = await requestBlogCmsTicket({ mode: "agent" }, controller.signal);
      setRateLimit(null);
      setExpiresInMs(ticket.expiresIn * 1000);
      setUrl(ticket.url);
      setStatus(BLOG_CMS_COPY.agentReady);
      requestFocus("copy");
    } catch (error) {
      const failure = classifyTicketFailure(error);
      if (failure.reason === "rate-limited") {
        failuresRef.current += 1;
        setRateLimit({ seconds: failure.retryAfterSeconds, key: failuresRef.current });
        setStatus(BLOG_CMS_COPY.rateLimitedTitle);
        // Create is disabled now, so focus would otherwise fall to <body> (U16).
        requestFocus("title");
      } else {
        setRateLimit(null);
        setStatus(BLOG_CMS_COPY.agentFailed);
        requestFocus("create");
      }
    } finally {
      setPending(false);
    }
  }

  async function handleCopy(link: string) {
    // Copy-once: the link leaves state whatever the clipboard says.
    setUrl(null);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setStatus(BLOG_CMS_COPY.agentCopied);
      requestFocus("close");
    } catch {
      setStatus(BLOG_CMS_COPY.agentCopyFailed);
      requestFocus("create");
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle ref={titleRef} tabIndex={-1}>
          {BLOG_CMS_COPY.agentTitle}
        </DialogTitle>
        <DialogDescription>{BLOG_CMS_COPY.agentDescription}</DialogDescription>
      </DialogHeader>
      <p role="status" className="text-sm">
        {status}
      </p>
      <DialogFooter className="gap-2">
        {url !== null ? (
          <Button ref={copyRef} variant="outline" onClick={() => void handleCopy(url)}>
            {BLOG_CMS_COPY.agentCopy}
          </Button>
        ) : copied ? null : rateLimit !== null ? (
          <RateLimitedCreate
            key={rateLimit.key}
            seconds={rateLimit.seconds}
            onCreate={() => void handleCreate()}
            ref={createRef}
          />
        ) : (
          <Button
            ref={createRef}
            variant="outline"
            onClick={() => void handleCreate()}
            disabled={pending}
          >
            {BLOG_CMS_COPY.agentCreate}
          </Button>
        )}
        <Button ref={closeRef} variant="outline" onClick={onClose}>
          {BLOG_CMS_COPY.agentClose}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * "Authorise AI agent" (C2 agent mode). It mints a copy-once sign-in link for the agent's dedicated
 * browser profile. The link is never rendered as text or as an `href`, never stored, and cleared on
 * copy, on close and at `expiresIn`.
 */
export function AgentLinkDialog({ open, onOpenChange, returnFocusRef }: AgentLinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* hideClose: the footer's Close is the one close button, so no two share a name. */}
      <DialogContent
        className="max-w-md"
        hideClose
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <AgentLinkBody onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
